import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  PlayArrow as TriggerIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';

// Theme color for Log Export Settings page
const accentColor = '#5D6D7E';

// Reusable input styles
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: accentColor },
    '&.Mui-focused fieldset': { borderColor: accentColor },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: accentColor },
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
    maxWidth: '800px',
  },
};

const LogExportSettings = () => {
  const isReadOnly = useReadOnlyMode();
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    bucketName: '',
    prefix: '',
    exportTime: '02:00',
    lastExportTimestamp: null,
    lastExportStatus: '',
    lastExportError: '',
  });
  const [savedConfig, setSavedConfig] = useState(null); // Track what's actually saved in the database
  const [history, setHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [callerIdentity, setCallerIdentity] = useState(null);
  const [loadingCallerIdentity, setLoadingCallerIdentity] = useState(false);

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getS3ExportConfig();
      if (response.success && response.data) {
        const configData = {
          enabled: response.data.enabled || false,
          bucketName: response.data.bucketName || '',
          prefix: response.data.prefix || '',
          exportTime: response.data.exportTime || '02:00',
          lastExportTimestamp: response.data.lastExportTimestamp,
          lastExportStatus: response.data.lastExportStatus || '',
          lastExportError: response.data.lastExportError || '',
        };
        setConfig(configData);
        // Track the saved state - only set if bucketName exists (meaning it was actually saved)
        if (response.data.bucketName) {
          setSavedConfig(configData);
        }
      }
    } catch (error) {
      toast.error('Failed to load S3 export configuration');
      console.error('Failed to load S3 export config:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch export history
  const fetchHistory = useCallback(async () => {
    try {
      const response = await apiService.getS3ExportHistory({
        page: historyPagination.page + 1, // API uses 1-indexed pages
        pageSize: historyPagination.pageSize,
      });
      if (response.success && response.data) {
        setHistory(response.data.records || []);
        setHistoryPagination(prev => ({
          ...prev,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch export history:', error);
    }
  }, [historyPagination.page, historyPagination.pageSize]);

  useEffect(() => {
    fetchConfig();
    fetchHistory();
  }, [fetchConfig, fetchHistory]);

  // Handle save
  const handleSave = async () => {
    if (!config.bucketName) {
      toast.error('Bucket name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await apiService.updateS3ExportConfig({
        enabled: config.enabled,
        bucketName: config.bucketName,
        prefix: config.prefix,
        exportTime: config.exportTime,
      });

      if (response.success) {
        toast.success('S3 export configuration saved');
        // Update saved config state so trigger button appears
        setSavedConfig({
          ...config,
          enabled: config.enabled,
        });
        fetchConfig();
      } else {
        toast.error(response.message || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
      console.error('Failed to save config:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle test connection
  const handleTestConnection = async () => {
    if (!config.bucketName) {
      toast.error('Please enter bucket name');
      return;
    }

    setTesting(true);
    try {
      const response = await apiService.testS3Connection(
        config.bucketName,
        config.prefix
      );

      if (response.success && response.data?.success) {
        const region = response.data?.region;
        toast.success(`Connection test successful! Detected region: ${region}`);
      } else {
        toast.error(response.data?.message || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
      console.error('Connection test failed:', error);
    } finally {
      setTesting(false);
    }
  };

  // Handle manual trigger
  const handleTriggerExport = async () => {
    setTriggering(true);
    try {
      const response = await apiService.triggerS3Export();
      if (response.success) {
        toast.success('Export triggered successfully');
        setTimeout(() => {
          fetchConfig();
          fetchHistory();
        }, 2000);
      } else {
        toast.error(response.message || 'Failed to trigger export');
      }
    } catch (error) {
      toast.error('Failed to trigger export');
      console.error('Failed to trigger export:', error);
    } finally {
      setTriggering(false);
    }
  };

  // Fetch caller identity for bucket policy
  const fetchCallerIdentity = async () => {
    setLoadingCallerIdentity(true);
    try {
      const response = await apiService.getS3CallerIdentity();
      if (response.success && response.data) {
        setCallerIdentity(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch caller identity:', error);
      toast.error('Failed to fetch AWS role information');
    } finally {
      setLoadingCallerIdentity(false);
    }
  };

  // Handle opening policy dialog
  const handleOpenPolicyDialog = () => {
    setShowPolicyDialog(true);
    if (!callerIdentity) {
      fetchCallerIdentity();
    }
  };

  // Generate bucket policy
  const generateBucketPolicy = () => {
    const bucketArn = `arn:aws:s3:::${config.bucketName || 'YOUR-BUCKET-NAME'}`;
    const resourcePath = config.prefix
      ? `${bucketArn}/${config.prefix}/*`
      : `${bucketArn}/*`;

    // Extract role ARN from caller identity (convert assumed-role ARN to role ARN)
    let roleArn = 'arn:aws:iam::ACCOUNT_ID:role/ROLE_NAME';
    if (callerIdentity?.arn) {
      // ARN format: arn:aws:sts::ACCOUNT_ID:assumed-role/ROLE_NAME/SESSION_NAME
      // We need: arn:aws:iam::ACCOUNT_ID:role/ROLE_NAME
      const arnParts = callerIdentity.arn.match(/arn:aws:sts::(\d+):assumed-role\/([^/]+)/);
      if (arnParts) {
        roleArn = `arn:aws:iam::${arnParts[1]}:role/${arnParts[2]}`;
      }
    }

    return `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "AllowPrismLogExport",
      "Effect": "Allow",
      "Principal": {
        "AWS": "${roleArn}"
      },
      "Action": [
        "s3:PutObject",
        "s3:DeleteObject"
      ],
      "Resource": "${resourcePath}"
    }
  ]
}`;
  };

  // Copy policy to clipboard
  const handleCopyPolicy = () => {
    navigator.clipboard.writeText(generateBucketPolicy());
    toast.success('Policy copied to clipboard');
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'partial':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F9FBFD',
        }}
      >
        <CircularProgress sx={{ color: accentColor }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{ minHeight: '100vh', padding: '32px', backgroundColor: '#F9FBFD' }}
    >
      {/* Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: accentColor, width: 48, height: 48 }}>
              <CloudUploadIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Log Export Settings
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                Configure S3 bucket for daily audit and access log exports
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => {
                  fetchConfig();
                  fetchHistory();
                }}
                disabled={loading}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#F0F4F8' },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Configuration Form */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1.1rem',
              color: '#253E66',
            }}
          >
            S3 Bucket Configuration
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                disabled={isReadOnly}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: accentColor,
                    '&:hover': { backgroundColor: `${accentColor}15` },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: accentColor,
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                  color: config.enabled ? accentColor : '#666666',
                }}
              >
                {config.enabled ? 'Export Enabled' : 'Export Disabled'}
              </Typography>
            }
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="S3 Bucket Name"
            value={config.bucketName}
            onChange={(e) => setConfig({ ...config, bucketName: e.target.value })}
            placeholder="my-logs-bucket"
            fullWidth
            required
            sx={inputStyle}
            helperText="The name of your S3 bucket (must exist and have proper permissions)"
          />

          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mb: 3, fontFamily: 'Metropolis, sans-serif' }}
          >
            Configure your S3 bucket to receive daily exports of audit and access logs.
            You must configure the bucket policy to allow our Prism to write objects.
            <Button
              size="small"
              onClick={handleOpenPolicyDialog}
              sx={{
                ml: 1,
                fontFamily: 'Metropolis, sans-serif',
                textTransform: 'none',
                color: accentColor,
              }}
            >
              View Required Policy
            </Button>
          </Alert>

          <TextField
            label="Path Prefix (Optional)"
            value={config.prefix}
            onChange={(e) => setConfig({ ...config, prefix: e.target.value })}
            placeholder="prism/logs"
            fullWidth
            sx={inputStyle}
            helperText="Files will be stored as: {prefix}/audit-logs/YYYY-MM-DD.json and {prefix}/access-logs/YYYY-MM-DD.json"
          />

          <TextField
            label="Export Time (UTC)"
            value={config.exportTime}
            onChange={(e) => setConfig({ ...config, exportTime: e.target.value })}
            placeholder="02:00"
            fullWidth
            sx={inputStyle}
            helperText="Daily export time in HH:MM format (24-hour, UTC). Default is 02:00 UTC."
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={testing || !config.bucketName}
            startIcon={testing ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            sx={{
              borderColor: '#E5E5E5',
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              px: 3,
              '&:hover': { borderColor: accentColor, color: accentColor },
            }}
          >
            Test Connection
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isReadOnly || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              backgroundColor: accentColor,
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              px: 3,
              '&:hover': { backgroundColor: '#4A5A6A' },
            }}
          >
            Save Configuration
          </Button>

          {savedConfig?.enabled && (
            <Button
              variant="outlined"
              onClick={handleTriggerExport}
              disabled={isReadOnly || triggering}
              startIcon={triggering ? <CircularProgress size={16} /> : <TriggerIcon />}
              sx={{
                borderColor: '#4698D3',
                color: '#4698D3',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                px: 3,
                '&:hover': { borderColor: '#3a7ab3', backgroundColor: 'rgba(70, 152, 211, 0.05)' },
              }}
            >
              Trigger Export Now
            </Button>
          )}
        </Box>

        {/* Show hint when enabled locally but not saved */}
        {config.enabled && !savedConfig?.enabled && (
          <Alert severity="warning" sx={{ mt: 2, fontFamily: 'Metropolis, sans-serif' }}>
            Click "Save Configuration" to enable exports. The "Trigger Export Now" button will appear after saving.
          </Alert>
        )}

        {/* Last Export Status */}
        {config.lastExportTimestamp && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#666666',
                  mb: 1,
                }}
              >
                Last Export
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#333333',
                  }}
                >
                  {formatTimestamp(config.lastExportTimestamp)}
                </Typography>
                <Chip
                  size="small"
                  label={config.lastExportStatus || 'unknown'}
                  color={getStatusColor(config.lastExportStatus)}
                  icon={
                    config.lastExportStatus === 'success' ? (
                      <SuccessIcon />
                    ) : config.lastExportStatus === 'partial' ? (
                      <WarningIcon />
                    ) : config.lastExportStatus === 'failed' ? (
                      <ErrorIcon />
                    ) : null
                  }
                  sx={{ fontFamily: 'Metropolis, sans-serif' }}
                />
              </Box>
              {config.lastExportError && (
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.8rem',
                    color: '#d32f2f',
                    mt: 1,
                  }}
                >
                  Error: {config.lastExportError}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Paper>
      </motion.div>

      {/* Export History */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid #E5E5E5',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
            }}
          >
            Export History
          </Typography>
          <Tooltip title="Refresh History">
            <IconButton size="small" onClick={fetchHistory}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Type
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Records
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  S3 Path
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Completed
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                      No export history available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((record, index) => (
                  <TableRow
                    key={record.id}
                    hover
                    sx={{
                      '@keyframes fadeInRow': {
                        from: { opacity: 0, transform: 'translateY(8px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                    }}
                  >
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      {record.exportDate}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      <Chip
                        size="small"
                        label={record.logType}
                        variant="outlined"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      {record.recordsExported}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={record.status}
                        color={getStatusColor(record.status)}
                        sx={{ fontFamily: 'Metropolis, sans-serif' }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {record.s3Key || '-'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>
                      {formatTimestamp(record.completedAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={historyPagination.total}
          page={historyPagination.page}
          onPageChange={(e, newPage) => setHistoryPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={historyPagination.pageSize}
          onRowsPerPageChange={(e) =>
            setHistoryPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #E5E5E5',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: 'Metropolis, sans-serif',
            },
          }}
        />
      </Paper>
      </motion.div>

      {/* Bucket Policy Dialog */}
      <Dialog
        open={showPolicyDialog}
        onClose={() => setShowPolicyDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            borderBottom: '1px solid #E5E5E5',
          }}
        >
          Required S3 Bucket Policy
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.9rem',
              color: '#666666',
              mb: 2,
            }}
          >
            Add this policy to your S3 bucket to allow log exports. The policy below is pre-configured
            with the correct role ARN for your Prism deployment.
          </Typography>
          {loadingCallerIdentity ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: accentColor }} />
            </Box>
          ) : (
            <Paper
              sx={{
                p: 2,
                backgroundColor: '#F5F5F5',
                borderRadius: '8px',
                border: '1px solid #E5E5E5',
              }}
            >
              <pre
                style={{
                  overflow: 'auto',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {generateBucketPolicy()}
              </pre>
            </Paper>
          )}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #E5E5E5', px: 3, py: 2 }}>
          <Button
            onClick={() => setShowPolicyDialog(false)}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleCopyPolicy}
            variant="contained"
            startIcon={<CopyIcon />}
            sx={{
              backgroundColor: accentColor,
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#4A5A6A' },
            }}
          >
            Copy Policy
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default LogExportSettings;
